import { useEffect, useState } from 'react';
import {
  DollarSign, RefreshCw, AlertCircle, CheckCircle2, Sparkles, TrendingUp, Tag, Percent,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { billingApi, type PricingData, type PricingRow } from '../../api/billing';

function money(c: number | null | undefined): string {
  if (c == null) return '—';
  return `$${(c / 100).toFixed(2)}`;
}

export default function PricingPanel() {
  const [data, setData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [suggestions, setSuggestions] = useState<Record<string, any>>({});
  const [suggesting, setSuggesting] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, { price: string; markup: string; mode: string }>>({});

  const load = async () => {
    setLoading(true);
    try { setData(await billingApi.getPricing()); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Could not load pricing.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const rows = [...(data?.plans ?? []), ...(data?.addons ?? [])];
  const draftFor = (r: PricingRow) => drafts[`${r.kind}:${r.key}`] ?? {
    price: (r.price_cents / 100).toFixed(2),
    markup: r.markup_pct != null ? String(r.markup_pct) : String(data?.default_markup_pct ?? 33),
    mode: r.pricing_mode,
  };
  const setDraft = (r: PricingRow, patch: Partial<{ price: string; markup: string; mode: string }>) =>
    setDrafts(prev => ({ ...prev, [`${r.kind}:${r.key}`]: { ...draftFor(r), ...patch } }));

  const save = async (r: PricingRow) => {
    const d = draftFor(r);
    setBusyKey(`${r.kind}:${r.key}`); setError(''); setSuccess('');
    try {
      await billingApi.setPricing({
        kind: r.kind, key: r.key, pricing_mode: d.mode as any,
        price_cents: Math.round(parseFloat(d.price || '0') * 100),
        markup_pct: parseFloat(d.markup || '0'),
      });
      setSuccess(`Updated ${r.name}.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not save price');
    } finally { setBusyKey(''); }
  };

  const refresh = async () => {
    setRefreshing(true); setError(''); setSuccess('');
    try {
      const res = await billingApi.refreshCosts();
      setSuccess(`RC costs refreshed — ${res.updated.length} changed, ${res.unchanged_count} unchanged.` +
        (res.below_cost.length ? ` ⚠ ${res.below_cost.length} below cost.` : ''));
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Cost refresh failed');
    } finally { setRefreshing(false); }
  };

  const suggest = async () => {
    const items = rows.filter(r => checked[`${r.kind}:${r.key}`]).map(r => ({
      kind: r.kind, key: r.key, markup_pct: parseFloat(draftFor(r).markup || '0') || undefined,
    }));
    if (!items.length) { setError('Check at least one product to suggest prices for.'); return; }
    setSuggesting(true); setError(''); setSuccess('');
    try {
      const res = await billingApi.suggestPrices(items);
      const map: Record<string, any> = {};
      res.suggestions.forEach(s => { map[`${s.kind}:${s.key}`] = s; });
      setSuggestions(map);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Suggest failed');
    } finally { setSuggesting(false); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  const renderRow = (r: PricingRow) => {
    const id = `${r.kind}:${r.key}`;
    const d = draftFor(r);
    const s = suggestions[id];
    return (
      <div key={id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <label className="flex min-w-0 items-start gap-2">
            <input type="checkbox" checked={!!checked[id]} onChange={e => setChecked(p => ({ ...p, [id]: e.target.checked }))}
              className="mt-1 h-4 w-4 rounded border-slate-300" />
            <span className="min-w-0">
              <span className="block truncate font-medium text-slate-950 dark:text-slate-100">{r.name}</span>
              <span className="text-xs capitalize text-slate-500">
                {r.kind}{r.category ? ` · ${r.category}` : ''}{r.billing_interval ? ` · ${r.billing_interval}` : ''}
              </span>
            </span>
          </label>
          <div className="text-right text-xs">
            <div className="text-slate-500">cost <span className="font-medium text-slate-700 dark:text-slate-300">{money(r.provider_cost_cents)}</span></div>
            <div className={r.below_cost ? 'font-semibold text-red-600' : 'text-slate-500'}>
              margin <span className="font-medium">{money(r.margin_cents)}</span>{r.below_cost ? ' ⚠ below cost' : ''}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">Mode</div>
            <select value={d.mode} onChange={e => setDraft(r, { mode: e.target.value })}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              <option value="fixed">Fixed</option>
              <option value="markup">Markup %</option>
            </select>
          </div>
          {d.mode === 'fixed' ? (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">Price (USD)</div>
              <input type="number" step="0.01" value={d.price} onChange={e => setDraft(r, { price: e.target.value })}
                className="w-28 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
            </div>
          ) : (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">Markup %</div>
              <input type="number" step="1" value={d.markup} onChange={e => setDraft(r, { markup: e.target.value })}
                className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
            </div>
          )}
          <div className="text-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Sell</div>
            <div className="font-semibold text-slate-950 dark:text-slate-100">{money(r.effective_price_cents)}</div>
          </div>
          <Button size="sm" onClick={() => save(r)} disabled={busyKey === id}>
            {busyKey === id ? <LoadingSpinner size="sm" /> : 'Save'}
          </Button>
          {s && (
            <div className={`text-xs ${s.ok ? 'text-brand-600 dark:text-brand-300' : 'text-slate-400'}`}>
              {s.ok ? <>suggested <strong>{money(s.suggested_price_cents)}</strong> <span className="text-slate-400">({s.source})</span></> : s.reason}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <DollarSign size={18} className="text-slate-500" />
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Pricing &amp; Margin</h2>
            <p className="text-sm text-slate-500">Set a fixed price or a markup % per product. Pull live ResellerClub costs. Default markup {data?.default_markup_pct}%.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={14} />{refreshing ? 'Refreshing…' : 'Refresh RC costs'}
          </Button>
          <Button size="sm" onClick={suggest} disabled={suggesting}>
            <Sparkles size={14} />{suggesting ? 'Thinking…' : 'Suggest price'}
          </Button>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <Card className="space-y-3 p-5">
        <div className="flex items-center gap-2 text-slate-500"><Tag size={16} /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Plans</h3></div>
        {data?.plans.map(renderRow)}
      </Card>

      <Card className="space-y-3 p-5">
        <div className="flex items-center gap-2 text-slate-500"><Percent size={16} /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Add-ons</h3></div>
        {data?.addons.map(renderRow)}
      </Card>

      <p className="text-xs text-slate-400">
        <TrendingUp size={12} className="mr-1 inline" />
        "Suggest price" is always sourced: domains use their live ResellerClub wholesale cost + your markup;
        owned products without a cost basis are left to a fixed price (we never invent a market number).
      </p>
    </div>
  );
}
